import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSettings } from "@/context/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPOINTMENT_TYPES = [
  "Free Tax Consultation",
  "Individual Tax Preparation",
  "Business Tax Consultation",
  "Bookkeeping Consultation",
  "Notary Appointment",
  "Online Digital Notary (RON)",
  "Non-Profit Services",
  "AI Automation Strategy Call",
  "Other",
];

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
];

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  appointment_type: "",
  preferred_date: "",
  preferred_time: "",
  alternate_date: "",
  alternate_time: "",
  message: "",
};

export default function BookingModal() {
  const settings = useSettings();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    const handler = (e) => {
      // If an external booking URL is configured, open it instead of the modal
      if (settings.booking_url) {
        window.open(settings.booking_url, "_blank", "noopener,noreferrer");
        return;
      }
      const preset = e?.detail?.type;
      setForm({ ...emptyForm, appointment_type: preset && APPOINTMENT_TYPES.includes(preset) ? preset : "" });
      setOpen(true);
    };
    window.addEventListener("open-booking", handler);
    return () => window.removeEventListener("open-booking", handler);
  }, [settings.booking_url]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.appointment_type || !form.preferred_date || !form.preferred_time) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Appointment request sent! We'll reach out shortly to confirm.");
      setOpen(false);
      setForm(emptyForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call 201-253-7674.");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-navy flex items-center gap-2">
            <CalendarCheck className="w-6 h-6 text-bronze" />
            Request an Appointment
          </DialogTitle>
          <DialogDescription className="font-body">
            Tell us what you need and your preferred time. We'll confirm by email or phone. All times are US Eastern (New York).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="b-name">Full Name *</Label>
              <Input id="b-name" data-testid="booking-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Doe" />
            </div>
            <div>
              <Label htmlFor="b-phone">Phone</Label>
              <Input id="b-phone" data-testid="booking-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(201) 555-0100" />
            </div>
          </div>

          <div>
            <Label htmlFor="b-email">Email *</Label>
            <Input id="b-email" type="email" data-testid="booking-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" />
          </div>

          <div>
            <Label>Service / Appointment Type *</Label>
            <Select value={form.appointment_type} onValueChange={(v) => update("appointment_type", v)}>
              <SelectTrigger data-testid="booking-type"><SelectValue placeholder="Select a service" /></SelectTrigger>
              <SelectContent>
                {APPOINTMENT_TYPES.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`booking-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="b-date">Preferred Date *</Label>
              <Input id="b-date" type="date" min={today} data-testid="booking-date" value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} />
            </div>
            <div>
              <Label>Preferred Time *</Label>
              <Select value={form.preferred_time} onValueChange={(v) => update("preferred_time", v)}>
                <SelectTrigger data-testid="booking-time"><SelectValue placeholder="Select time" /></SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="b-alt-date" className="text-gray-500">Alternate Date</Label>
              <Input id="b-alt-date" type="date" min={today} data-testid="booking-alt-date" value={form.alternate_date} onChange={(e) => update("alternate_date", e.target.value)} />
            </div>
            <div>
              <Label className="text-gray-500">Alternate Time</Label>
              <Select value={form.alternate_time} onValueChange={(v) => update("alternate_time", v)}>
                <SelectTrigger data-testid="booking-alt-time"><SelectValue placeholder="Select time" /></SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="b-message">Anything we should know?</Label>
            <Textarea id="b-message" data-testid="booking-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Briefly describe what you need help with..." rows={3} />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 font-medium"
            data-testid="booking-submit"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Request Appointment"}
          </Button>
          <p className="text-xs text-gray-500 text-center">This is a request. We'll contact you to confirm your appointment.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
